/**
 * EmailThread — the reading pane: toolbar + subject bar + messages + infobar.
 * Reacts to store.currentThreadId changes to fetch and render thread data.
 */

import { h, Fragment } from 'preact'
import { useRef } from 'preact/hooks'
import { htmlToPlainSnippet } from '@tshmanager/email-shared'
import { PersonAvatar } from './PersonAvatar.jsx'
import { ThreadMessage } from './ThreadMessage.jsx'
import { ThreadInfobar } from './ThreadInfobar.jsx'
import { ThreadToolbar } from './ThreadToolbar.jsx'
import { tagTextColor, timeAgo, threadTagIds } from '../../modules/email/email-helpers.js'

export function EmailThread({ store, i18n }) {
  const thread = store.thread.value
  const loading = store.threadLoading.value
  const currentId = store.currentThreadId.value
  const commentFormVisible = store.commentFormVisible.value
  const auditLogVisible = store.auditLogVisible.value
  const auditEvents = store.auditEvents.value
  const expandedMessages = store.expandedMessages.value

  const commentBodyRef = useRef(null)

  if (!currentId) {
    return (
      <div class="email-reading-pane-empty d-flex align-items-center justify-content-center h-100 text-muted">
        <div class="text-center">
          <i class="bi bi-envelope-open d-block mb-2 opacity-40" style="font-size:3rem" />
          <span class="small">{i18n?.t('emails.select_email')}</span>
        </div>
      </div>
    )
  }

  if (loading || !thread) {
    return (
      <div class="email-reading-pane-inner d-flex align-items-center justify-content-center text-muted">
        <div class="spinner-border me-2" role="status" />
        <span>{i18n?.t('helpers.loading')}</span>
      </div>
    )
  }

  const emailItems = (thread.items || []).filter(i => i.type === 'email')
  const commentItems = (thread.items || []).filter(i => i.type === 'comment')
  const participants = thread.participants || []
  const infobar = thread.infobar || {}
  // Always use bootstrapped store folders — thread.email_folders is [] on prefetch
  // (and may stay [] via HTTP cache), so never use it as the move-dropdown source.
  // Only folders belonging to this thread's mailbox are valid move targets.
  const mailboxId = thread.mailbox_id
    ?? (store.emails.value || []).find(e => e.id === thread.id)?.mailbox_id
    ?? null
  const folders = (store.emailFolders.value || []).filter(f =>
    mailboxId == null ? false : Number(f.mailbox_id) === Number(mailboxId)
  )
  // Prefetch omits people_with_user — fall back to shell-bootstrapped list
  const peopleWithUser = (thread.people_with_user?.length
    ? thread.people_with_user
    : store.peopleWithUser.value) || []

  async function submitComment(e) {
    e.preventDefault()
    const body = commentBodyRef.current?.value?.trim()
    if (!body) return
    await store.postComment(thread.id, body)
    if (commentBodyRef.current) commentBodyRef.current.value = ''
  }

  function mobileBack() {
    if (store.isThreadOnlyView.value) {
      window.close()
      return
    }
    store.showListPanel()
    store.clearCurrentThread()
  }

  return (
    <div class="email-reading-pane-inner">

      <ThreadToolbar
        thread={thread}
        store={store}
        i18n={i18n}
        folders={folders}
        peopleWithUser={peopleWithUser}
        onMobileBack={mobileBack}
      />

      {/* ── Subject bar ── */}
      <div class="email-thread-subject-bar px-2 px-md-4 pt-3 pb-2 border-bottom">
        <h4 class="mb-1 fw-semibold lh-sm">
          {thread.subject || i18n?.t('emails.no_subject')}
          {emailItems.length > 1 && (
            <span class="badge bg-secondary-subtle text-secondary fw-normal ms-1" style="font-size:0.6em;vertical-align:middle">
              {emailItems.length}
            </span>
          )}
        </h4>
        {(threadTagIds(thread).length > 0) && (
          <div class="d-flex flex-wrap gap-1 mt-1">
            {threadTagIds(thread).map(tagId => {
              const tag = (store.emailTags.value || []).find(t => t.id === tagId)
              if (!tag) return null
              const color = tag.color || '#6c757d'
              return (
                <span
                  key={tag.id}
                  class="badge rounded-pill"
                  style={{ backgroundColor: color, color: tagTextColor(color), fontSize: '0.7rem' }}
                >
                  {tag.tag}
                </span>
              )
            })}
          </div>
        )}
        {participants.length > 1 && (
          <div class="d-flex align-items-center gap-1 mt-1">
            {participants.map((p, i) => (
              <div key={p.id} style={{ marginLeft: i > 0 ? '-6px' : '0', zIndex: 10 - i, position: 'relative' }}>
                <PersonAvatar avatarUrl={p.avatar_url} name={p.name} size={22} showTooltip cssClass="border border-2 border-white" />
              </div>
            ))}
            <span class="text-muted small ms-2">
              {participants.slice(0, 3).map(p => p.name).join(', ')}
              {participants.length > 3 && <span class="text-muted"> +{participants.length - 3}</span>}
            </span>
          </div>
        )}
      </div>

      {/* ── Body row: messages + infobar ── */}
      <div class="email-thread-body">
        <div class="email-thread-main-column">
          <div id="email_thread_content" class="email-thread-scroll">
            <div class="email-thread-items" id="email_thread_items">

              {/* Render all items (emails + comments + drafts) sorted newest-first */}
              {(thread.items || []).map(item => {
                if (item.type === 'email') {
                  return (
                    <div key={`email-${item.email.id}`} class="border-bottom">
                      <ThreadMessage
                        emailItem={item.email}
                        isExpanded={!!expandedMessages[item.email.id]}
                        store={store}
                        i18n={i18n}
                      />
                    </div>
                  )
                } else if (item.type === 'comment') {
                  return <ThreadComment key={`comment-${item.comment.id}`} comment={item.comment} store={store} i18n={i18n} />
                } else if (item.type === 'draft') {
                  return <DraftCard key={`draft-${item.draft.id}`} draft={item.draft} store={store} i18n={i18n} />
                }
                return null
              })}

              {/* Audit events (loaded on toggle) */}
              {auditLogVisible && (
                <div id="email_audit_events">
                  {store.auditEventsLoading.value ? (
                    <div class="text-center py-2 text-muted">
                      <div class="spinner-border spinner-border-sm" role="status" />
                    </div>
                  ) : (
                    auditEvents.map(event => (
                      <AuditEvent key={event.id} event={event} i18n={i18n} />
                    ))
                  )}
                </div>
              )}

              {/* Audit log toggle */}
              <div class="d-flex align-items-center justify-content-center py-2">
                <div class="form-check form-switch small text-muted">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    id="toggle_audit_log"
                    checked={auditLogVisible}
                    onChange={e => store.toggleAuditLog(e.target.checked)}
                  />
                  <label class="form-check-label" for="toggle_audit_log">
                    {i18n?.t('emails.show_audit_log')}
                  </label>
                </div>
              </div>

            </div>
          </div>

          {/* Reply / comment forms */}
          <div class="email-reply-sticky">
            {commentFormVisible && (
              <CommentForm
                thread={thread}
                store={store}
                i18n={i18n}
                bodyRef={commentBodyRef}
                onSubmit={submitComment}
              />
            )}
            {!store.composeOpen.value && !commentFormVisible && (
              <div
                class="d-flex align-items-center gap-2 px-3 py-2 email-reply-trigger border-top bg-body"
                role="button"
                tabIndex={0}
                onClick={() => store.openCompose('reply', thread.id)}
                onKeyDown={e => e.key === 'Enter' && store.openCompose('reply', thread.id)}
              >
                <div class="flex-grow-1 border rounded px-3 py-2 text-muted small bg-body-tertiary email-reply-placeholder">
                  {i18n?.t('emails.reply_placeholder')}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Infobar */}
        <div class="email-infobar border-start">
          <button
            class="btn btn-sm btn-link text-muted w-100 d-md-none py-2 border-bottom"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#email_infobar_collapse"
          >
            <i class="bi bi-info-circle me-1" />
            {i18n?.t('emails.show_infobar')}
            <i class="bi bi-chevron-down ms-1" style="font-size:0.7rem" />
          </button>
          <div class="collapse d-md-block" id="email_infobar_collapse">
            <ThreadInfobar infobar={infobar} emailId={thread.id} store={store} i18n={i18n} />
          </div>
        </div>

      </div>
    </div>
  )
}

function ThreadComment({ comment, store, i18n }) {
  async function handleDelete(e) {
    e.preventDefault()
    if (!confirm(i18n?.t('helpers.confirm_destroy'))) return
    await store.deleteComment(comment.id)
  }

  return (
    <div id={`comment_${comment.id}_thread_item`} class="email-thread-comment border-bottom">
      <div class="d-flex align-items-start gap-2 p-3">
        <PersonAvatar avatarUrl={comment.avatar_url} name={comment.person_name} size={32} cssClass="flex-shrink-0" />
        <div class="flex-grow-1">
          <div class="d-flex align-items-baseline justify-content-between mb-1">
            <div>
              <span class="fw-semibold small">{comment.person_name}</span>
              <span class="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle ms-1 small">
                <i class="bi bi-sticky small" />
                {i18n?.t('emails.internal_note')}
              </span>
            </div>
            <div class="d-flex align-items-center gap-2 flex-shrink-0">
              <span class="text-muted small">{timeAgo(comment.created_at)}</span>
              {comment.is_own && (
                <a href="#" class="btn btn-link p-0 text-muted small" onClick={handleDelete}>
                  <i class="bi bi-x-lg" />
                </a>
              )}
            </div>
          </div>
          <div class="email-comment-body">{comment.body}</div>
        </div>
      </div>
    </div>
  )
}

function CommentForm({ thread, store, i18n, bodyRef, onSubmit }) {
  return (
    <div id="email_comment_form" class="px-3 py-2">
      <div class="email-comment-form border rounded bg-warning-subtle d-flex flex-column">
        <div class="d-flex align-items-start gap-2 p-2 min-h-0">
          <div class="flex-grow-1 min-w-0 d-flex flex-column min-h-0">
            <div class="small fw-semibold text-warning-emphasis mb-1 flex-shrink-0">
              <i class="bi bi-sticky small me-1" />
              {i18n?.t('emails.add_internal_note')}
            </div>
            <form onSubmit={onSubmit} class="d-flex flex-column min-h-0">
              <input type="hidden" name="email_comment[email_id]" value={thread.id} />
              <textarea
                ref={bodyRef}
                class="form-control"
                placeholder={i18n?.t('emails.note_placeholder')}
                rows={2}
                required
                style="resize:vertical;max-height:12rem"
              />
              <div class="d-flex justify-content-end gap-1 mt-2 flex-shrink-0">
                <button type="button" class="btn btn-sm btn-outline-secondary" onClick={store.hideCommentForm}>
                  {i18n?.t('helpers.actions.cancel')}
                </button>
                <button type="submit" class="btn btn-sm btn-warning">
                  {i18n?.t('emails.save_note')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

function DraftCard({ draft, store, i18n }) {
  const label = draft.draft_type === 'forward'
    ? (i18n?.t('emails.forward') || 'Továbbítás')
    : (i18n?.t('emails.reply') || 'Válasz')

  const preview = htmlToPlainSnippet(draft.body_html, 120)

  const toList = (draft.to_addresses || []).join(', ')

  function handleClick() {
    store.openComposeFromDraft(draft)
  }

  return (
    <div
      class="email-draft-card border-bottom px-3 py-2 d-flex align-items-start gap-2"
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={e => e.key === 'Enter' && handleClick()}
      style="cursor:pointer"
    >
      <div class="flex-shrink-0 mt-1 text-warning">
        <i class="bi bi-pencil-square" style="font-size:1.1rem" />
      </div>
      <div class="flex-grow-1 min-width-0">
        <div class="d-flex align-items-center gap-2 mb-1">
          <span class="badge bg-warning text-dark small">
            {i18n?.t('emails.draft')}
          </span>
          <span class="text-muted small">{label}</span>
          {toList && (
            <span class="text-muted small ms-1">
              → {toList}
            </span>
          )}
        </div>
        {preview && (
          <div class="text-muted small text-truncate">{preview}</div>
        )}
      </div>
      <div class="flex-shrink-0 text-muted small align-self-center">
        <i class="bi bi-chevron-right" />
      </div>
    </div>
  )
}

function AuditEvent({ event, i18n }) {
  return (
    <div class="email-thread-audit-event py-2 px-3 border-bottom">
      <div class="d-flex align-items-center gap-2 text-muted small">
        {event.person_avatar_url
          ? <PersonAvatar avatarUrl={event.person_avatar_url} name={event.person_name} size={20} cssClass="flex-shrink-0 opacity-75" />
          : <div style="width:20px;height:20px" class="flex-shrink-0"><i class="bi bi-gear small" /></div>
        }
        <span>{event.summary}</span>
        <span class="ms-auto flex-shrink-0">{timeAgo(event.created_at)}</span>
      </div>
    </div>
  )
}

